import { useEffect, useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { parseMoney } from '@/components/Plan/MoneyInput'
import { formatRevenueCompact, formatRevenueFull } from './formatters'
import { deriveGrowth, planFromGrowth } from './channelGrowth'

type Props = {
  past: number
  plan: number
  currency?: string | null
  onChange: (next: number) => void
  readOnly?: boolean
  rowGap?: number
  columnDelta?: number
  // Actual sales for this cell's channel, on a revision. Rendered as a first
  // line above Past when showActual is set, and counted into Growth, which
  // reads on the full year.
  actual?: number
  showActual?: boolean
}

const inputCls =
  'w-full text-right border rounded-md px-1 py-0.5 bg-background tabular-nums outline-none transition-colors hover:border-border focus:border-secondary'

const GUTTER = 18

// First line of a cell on a revision: this channel's actual sales for the
// plan's actual months. Shared between the read-only and editable branches.
function ActualLine({ actual, currency }: { actual: number; currency?: string | null }) {
  return (
    <div className="flex items-center gap-1" style={{ height: 20 }}>
      <div className="flex-1 text-right tabular-nums text-muted-foreground">
        {actual > 0 ? formatRevenueCompact(actual, currency) : '—'}
      </div>
      <div className="shrink-0" style={{ width: GUTTER, height: GUTTER }} aria-hidden />
    </div>
  )
}

// Last line of a cell on a revision: the channel's full year, sold months
// plus the remaining-months plan.
function PlanTotalLine({ total, currency }: { total: number; currency?: string | null }) {
  return (
    <div className="flex items-center gap-1" style={{ height: 20 }} title={formatRevenueFull(total, currency)}>
      <div className="flex-1 text-right tabular-nums text-muted-foreground">
        {total > 0 ? formatRevenueCompact(total, currency) : '—'}
      </div>
      <div className="shrink-0" style={{ width: GUTTER, height: GUTTER }} aria-hidden />
    </div>
  )
}

export default function ChannelCell({ past, plan, currency, onChange, readOnly = false, rowGap = 0, columnDelta = 0, actual = 0, showActual = false }: Props) {
  const [growthFocused, setGrowthFocused] = useState(false)
  const [growthInput, setGrowthInput] = useState<string>(() => deriveGrowth({ past, plan, actual }))
  const [planFocused, setPlanFocused] = useState(false)
  const [planDraft, setPlanDraft] = useState<string>(() => (plan === 0 ? '' : formatRevenueFull(plan, currency)))

  useEffect(() => {
    if (!planFocused) setPlanDraft(plan === 0 ? '' : formatRevenueFull(plan, currency))
    if (!growthFocused) setGrowthInput(deriveGrowth({ past, plan, actual }))
  }, [plan, past, actual, currency, planFocused, growthFocused])

  const onGrowthChange = (raw: string) => {
    setGrowthInput(raw)
    if (raw === '') {
      onChange(0)
      return
    }
    if (past <= 0 || raw === '-' || raw.endsWith('.')) return
    const pct = Number(raw)
    if (Number.isNaN(pct)) return
    onChange(planFromGrowth({ past, pct, actual }))
  }

  const commitPlanBlur = () => {
    setPlanFocused(false)
    const trimmed = planDraft.trim()
    if (trimmed === '') {
      if (plan !== 0) onChange(0)
      return
    }
    const parsed = parseMoney(trimmed, currency)
    if (parsed == null) return
    const next = Math.max(0, Math.round(parsed))
    if (next !== plan) onChange(next)
  }

  const gutter = <div className="shrink-0" style={{ width: GUTTER, height: GUTTER }} aria-hidden />

  const columnDeltaLabel =
    columnDelta > 0
      ? `${formatRevenueCompact(columnDelta, currency)} over`
      : columnDelta < 0
        ? `${formatRevenueCompact(Math.abs(columnDelta), currency)} short`
        : ''
  const columnDeltaColor =
    columnDelta !== 0 ? 'var(--destructive, #c53030)' : 'var(--muted-foreground)'

  if (readOnly) {
    const growthDisplay = deriveGrowth({ past, plan, actual })
    return (
      <td className="px-2 pt-1.5 pb-1.5 border-b border-border align-top">
        <div className="flex flex-col gap-1.5">
          {showActual && <ActualLine actual={actual} currency={currency} />}
          <div className="flex items-center gap-1" style={{ height: 20 }}>
            <div className="flex-1 text-right tabular-nums text-muted-foreground">
              {past > 0 ? formatRevenueCompact(past, currency) : '—'}
            </div>
            {gutter}
          </div>
          <div className="flex items-center gap-1" style={{ height: 24 }}>
            <div className="flex-1 text-right tabular-nums text-muted-foreground">
              {growthDisplay === '' ? '—' : `${growthDisplay}%`}
            </div>
            {gutter}
          </div>
          <div className="flex items-center gap-1" style={{ height: 24 }}>
            <div className="flex-1 text-right tabular-nums text-foreground">
              {plan === 0 ? '—' : formatRevenueCompact(plan, currency)}
            </div>
            {gutter}
          </div>
          {showActual && <PlanTotalLine total={actual + plan} currency={currency} />}
          {columnDeltaLabel && (
            <div
              className="text-right text-[10px] tabular-nums"
              style={{ color: columnDeltaColor }}
            >
              {columnDeltaLabel}
            </div>
          )}
        </div>
      </td>
    )
  }

  const canFill = rowGap > 0
  const canDeduct = rowGap < 0
  const deductAmount = canDeduct ? Math.min(Math.abs(rowGap), plan) : 0

  return (
    <td className="px-2 pt-1.5 pb-1.5 border-b border-border align-top">
      <div className="flex flex-col gap-1.5">
        {showActual && <ActualLine actual={actual} currency={currency} />}
        <div className="flex items-center gap-1" style={{ height: 20 }}>
          <div className="flex-1 text-right tabular-nums text-muted-foreground">
            {past > 0 ? formatRevenueCompact(past, currency) : '—'}
          </div>
          {gutter}
        </div>
        <div className="flex items-center gap-1" style={{ height: 24 }}>
          <div className="relative flex-1 min-w-0">
            <input
              className={`${inputCls} pr-5`}
              value={growthInput}
              onFocus={() => setGrowthFocused(true)}
              onBlur={() => setGrowthFocused(false)}
              onChange={(e) => onGrowthChange(e.target.value)}
            />
            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-muted-foreground">%</span>
          </div>
          {gutter}
        </div>
        <div className="flex items-center gap-1" style={{ height: 24 }}>
          <input
            type="text"
            className={`${inputCls} flex-1 min-w-0`}
            value={planFocused ? planDraft : (plan === 0 ? '' : formatRevenueFull(plan, currency))}
            onFocus={(e) => {
              setPlanDraft(plan === 0 ? '' : formatRevenueFull(plan, currency))
              setPlanFocused(true)
              setTimeout(() => e.target.select(), 0)
            }}
            onBlur={commitPlanBlur}
            onChange={(e) => setPlanDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                ;(e.target as HTMLInputElement).blur()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setPlanFocused(false)
              }
            }}
            placeholder="0"
          />
          <div className="shrink-0" style={{ width: GUTTER, height: GUTTER }}>
            {canFill && (
              <button
                type="button"
                onClick={() => onChange(Math.max(0, plan + rowGap))}
                title={`Fill remaining ${formatRevenueFull(rowGap, currency)}`}
                aria-label="Fill remaining"
                className="inline-flex items-center justify-center rounded transition-colors hover:bg-accent w-full h-full"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <Plus size={14} strokeWidth={2} />
              </button>
            )}
            {canDeduct && deductAmount > 0 && (
              <button
                type="button"
                onClick={() => onChange(Math.max(0, plan - deductAmount))}
                title={`Deduct excess ${formatRevenueFull(deductAmount, currency)}`}
                aria-label="Deduct excess"
                className="inline-flex items-center justify-center rounded transition-colors hover:bg-accent w-full h-full"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <Minus size={14} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
        {showActual && <PlanTotalLine total={actual + plan} currency={currency} />}
        {columnDeltaLabel && (
          <div
            className="flex items-center gap-1"
            title={`Children sum ${columnDelta > 0 ? 'exceeds' : 'falls short of'} this cell by ${formatRevenueFull(Math.abs(columnDelta), currency)}`}
          >
            <div className="flex-1 text-right text-[10px] tabular-nums" style={{ color: columnDeltaColor }}>
              {columnDeltaLabel}
            </div>
            {gutter}
          </div>
        )}
      </div>
    </td>
  )
}

